import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CornerUpRight,
  Forward,
  Mail,
  MoreVertical,
  Reply,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useComposerState } from './composer-state';
import {
  ClampedContent,
  MessageBubble,
  MessageBubbleFooter,
  MessageBubbleSubject,
  MessageRow,
  ON_SURFACE_CONTROL,
  ON_SURFACE_LINK,
} from './message-bubble';
import { EmailTextContent } from './email-text-content';
import type { EmailActivity } from './types';
import { DeliveryStatus } from '~/components/delivery-status';
import { QuotedTextButton } from '~/components/quoted-text-button';
import { EmailAttachments } from '~/components/email-attachments';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { formatDateTime } from '~/lib/dates';
import { useTRPC } from '~/lib/trpc';
import { cn } from '~/lib/utils';

/** No sender or recipient recorded on a forward. */
const UNKNOWN_PARTY = '—';

export function EmailActivityMessage({
  activity,
}: {
  activity: EmailActivity;
}) {
  const { t } = useTranslation('tasks');
  const { state, setState } = useComposerState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isTrimmedShown, setIsTrimmedShown] = useState(false);
  const isOutbound = activity.direction === 'outbound';
  const trpc = useTRPC();
  // Once the clinic sends from its own mailbox, this mail is history: the
  // transactional sender no longer replies or forwards for it.
  const { data: emailProvider } = useQuery(
    trpc.config.queries.getEmailProviderKind.queryOptions(),
  );
  const canSend = emailProvider === 'mailerSend';
  /**
   * The mail as it was sent, and null when that is no more than what it says.
   * `emailSentText` decides, so the button cannot appear over nothing — the
   * comparison has a line-ending trap in it that belongs in one place.
   */
  const trimmedText = activity.sentText;
  const replyTarget = canSend ? activity.replyTarget : null;
  const isReplyTarget =
    state.view === 'email' &&
    state.mode === 'reply' &&
    state.target.emailId === activity.emailId;
  /** Collapsing takes the quoted chain's control away with it, so it drops the
   *  chain too rather than leaving it shown with no way back. */
  const toggleExpanded = () => {
    if (isExpanded) {
      setIsTrimmedShown(false);
    }

    setIsExpanded(!isExpanded);
  };

  return (
    <MessageRow isOutbound={isOutbound} handledBy={activity.handledBy}>
      <MessageBubble
        isOutbound={isOutbound}
        isHighlighted={isReplyTarget}
        actions={
          <EmailActions
            activity={activity}
            onForward={
              canSend
                ? () =>
                    setState({
                      view: 'email',
                      mode: 'forward',
                      target: { emailId: activity.emailId },
                    })
                : null
            }
            onReply={
              replyTarget
                ? () =>
                    setState({
                      view: 'email',
                      mode: 'reply',
                      target: replyTarget,
                    })
                : null
            }
          />
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {activity.subject && (
              <MessageBubbleSubject subject={activity.subject} />
            )}
            <ClampedContent
              isExpanded={isExpanded}
              isOverflowing={isOverflowing}
              onOverflowChange={setIsOverflowing}
              className="min-h-6"
            >
              {/* Text, not the HTML — the same bubble a text message gets. The
                  text is what the message says, so this surface needs no
                  iframe, sanitizer or CSP of its own, and `Open in mailbox`
                  covers mail that wants real rendering. */}
              <EmailTextContent
                text={
                  isTrimmedShown && trimmedText !== null
                    ? trimmedText
                    : activity.body
                }
              />
            </ClampedContent>
          </div>
          <EmailAttachments
            attachments={activity.attachments}
            onPrimary={isOutbound}
          />
        </div>
        <MessageBubbleFooter
          icon={Mail}
          label={formatDateTime(activity.createdAt)}
          status={<DeliveryStatus badge={activity.deliveryBadge} />}
          leading={
            <span className="flex min-w-0 items-center gap-2">
              {(isOverflowing || isExpanded) && (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className={ON_SURFACE_LINK}
                  onClick={toggleExpanded}
                >
                  {isExpanded ? t('Show less') : t('Show more')}
                </Button>
              )}
              {/* The quoted chain is a second, larger reveal — it only earns a
                  control once the message itself is fully on screen. */}
              {isExpanded && trimmedText !== null && !isTrimmedShown && (
                <QuotedTextButton
                  onPrimary={isOutbound}
                  onShow={() => setIsTrimmedShown(true)}
                />
              )}
              {isReplyTarget && (
                <Badge tone="primary">{t('Replying to')}</Badge>
              )}
            </span>
          }
        />
        <EmailForwards activity={activity} isOutbound={isOutbound} />
      </MessageBubble>
    </MessageRow>
  );
}

function EmailActions({
  activity,
  onForward,
  onReply,
}: {
  activity: EmailActivity;
  onForward: (() => void) | null;
  onReply: (() => void) | null;
}) {
  const { t } = useTranslation('tasks');
  /** Inbound mail is read as a thread, outbound as the single message that was
   *  sent — two different routes to what reads as one action. */
  const openInMailbox =
    activity.direction === 'inbound' ? (
      <Link
        to="/mailbox/incoming/$threadId"
        params={{ threadId: activity.threadId }}
        className="flex w-full items-center gap-2"
      >
        <Mail className="size-4" strokeWidth={2.5} />
        {t('Open in mailbox')}
      </Link>
    ) : (
      <Link
        to="/mailbox/sent/$emailId"
        params={{ emailId: activity.emailId }}
        className="flex w-full items-center gap-2"
      >
        <Mail className="size-4" strokeWidth={2.5} />
        {t('Open in mailbox')}
      </Link>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t('More actions')}
          className={ON_SURFACE_CONTROL}
        >
          <MoreVertical strokeWidth={2.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>{openInMailbox}</DropdownMenuItem>
        {onReply && (
          <DropdownMenuItem onSelect={onReply}>
            <Reply className="size-4" strokeWidth={2.5} />
            {t('Reply')}
          </DropdownMenuItem>
        )}
        {onForward && (
          <DropdownMenuItem onSelect={onForward}>
            <Forward className="size-4" strokeWidth={2.5} />
            {t('Forward')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmailForwards({
  activity,
  isOutbound,
}: {
  activity: EmailActivity;
  isOutbound: boolean;
}) {
  const { t } = useTranslation('tasks');

  if (activity.forwards.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 border-t pt-2',
        isOutbound ? 'border-white/10' : 'border-border',
      )}
    >
      {activity.forwards.map((forward) => (
        <Link
          key={forward.emailId}
          to="/mailbox/sent/$emailId"
          params={{ emailId: forward.emailId }}
          className="flex items-center gap-1.5 text-2xs opacity-80 transition-opacity hover:opacity-100"
        >
          <CornerUpRight className="size-3 shrink-0" strokeWidth={2.5} />
          <span className="min-w-0 flex-1 truncate">
            {t('FWD: {{sender}} to {{recipient}}', {
              recipient: forward.to ?? UNKNOWN_PARTY,
              sender: forward.sentByName ?? UNKNOWN_PARTY,
            })}
          </span>
          <span className="shrink-0">{formatDateTime(forward.createdAt)}</span>
        </Link>
      ))}
    </div>
  );
}
